import { motion } from "framer-motion";
import { Camera, Plane } from "lucide-react";
import { beyondItems, beyondGallery } from "@/data/portfolio";
import { PhotoBelt } from "./PhotoBelt";

const icons = { photography: Camera, travel: Plane };

const TiltCard = ({ item, i }) => {
  const Icon = icons[item.icon] || Camera;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const rx = ((e.clientY - r.top) / r.height - 0.5) * -9;
    const ry = ((e.clientX - r.left) / r.width - 0.5) * 9;
    e.currentTarget.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };
  const onLeave = (e) => {
    e.currentTarget.style.transform = "perspective(900px) rotateX(0deg) rotateY(0deg)";
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="card-glass spotlight-card rounded-2xl border border-white/10 p-6 transition-[transform,border-color,box-shadow] duration-300 will-change-transform hover:border-cyan-400/40 hover:glow-cyan"
      data-testid={`beyond-card-${item.icon}`}
    >
      <div className="flex items-center gap-3">
        <span className="rounded-full border border-violet-500/40 bg-violet-500/10 p-2.5 text-violet-300">
          <Icon size={18} />
        </span>
        <h3 className="font-display text-lg font-bold text-slate-50">{item.title}</h3>
      </div>
      <p className="mt-4 text-sm leading-relaxed text-slate-400">{item.text}</p>
      <div className="mt-5 h-44 overflow-hidden rounded-xl border border-white/10">
        <img
          src={item.photo}
          alt={item.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 hover:scale-110"
          data-testid={`beyond-photo-${item.icon}`}
        />
      </div>
    </motion.div>
  );
};

export const BeyondCode = () => {
  return (
    <section id="beyond" className="relative py-24 lg:py-36" data-testid="beyond-section">
      <div className="pointer-events-none absolute right-1/4 top-0 h-[350px] w-[500px] rounded-full orb-cyan" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-400">
            {"// 06 — Off Duty"}
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
            Life beyond<br />the <span className="gradient-text">terminal</span>
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
            The same curiosity that drives my models drives everything else — light, and new cities.
          </p>
        </motion.div>

        <PhotoBelt />

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {beyondItems.map((item, i) => (
            <TiltCard key={item.icon} item={item} i={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16"
        >
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-violet-300">
            {"// Through my lens"}
          </span>
          <div
            className="mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4"
            data-testid="lens-gallery"
          >
            {beyondGallery.map((shot, i) => (
              <a
                key={shot.src}
                href={shot.src}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`lens-photo-${i}`}
                className="group relative h-72 w-56 shrink-0 snap-center overflow-hidden rounded-2xl border border-white/10 transition-colors duration-300 hover:border-cyan-400/50 sm:h-80 sm:w-64"
              >
                <img
                  src={shot.src}
                  alt={shot.caption}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050508]/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <span className="absolute inset-x-3 bottom-3 translate-y-2 font-mono text-[10px] uppercase tracking-widest text-cyan-300 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  {shot.caption}
                </span>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
